import { canManageSubscription } from '../../lib/universal/subscription-access';
import React, { ReactNode, useEffect, useState } from 'react';
import Head from 'next/head';
import { Logo } from '../Logo';
import useLocale from '../../lib/hooks/use-locale';
import { useAuthContext } from '../../lib/context/auth-user-context';
import useFirebaseAuth from '../../lib/hooks/use-firebase-auth';

import { useRouter } from 'next/router';
import { Spinner } from '../loaders/Spinner';

type Props = {
  children?: ReactNode;
  title: string;
  groupPage?: 'subscription' | 'member-invite-template';
};

const Layout = ({ children, title, groupPage = 'subscription' }: Props) => {
  const router = useRouter();

  const { groupHint } = router.query;

  const { t, locale } = useLocale();
  const { richUser, loading, signOut } = useFirebaseAuth(groupHint as string);
  const currentGid = richUser?.currentGroupSettings?.id;
  const currentRole = currentGid ? richUser?.userSettings?.groups?.[currentGid]?.role : undefined;
  const hasAdminAccess = ['owner', 'admin', 'sponsored_admin'].includes(currentRole);

  return loading ? (
    <></>
  ) : (
    <div className="font-quicksand">
      <Head>
        <title>{title}</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <link
          href="https://fonts.googleapis.com/icon?family=Material+Icons"
          rel="stylesheet"
        ></link>
      </Head>
      <Header signOut={() => signOut()} groupPage={groupPage} />

      <div className="flex flex-col md:flex-row">
        <aside className="hidden min-w-[200px] bg-white px-3 py-1 md:block md:min-h-screen md:py-3">
          <nav>
            <ul className="mb-5">
              <li>
                <a href="/" className="cm-link flex items-center p-4 transition-all">
                  <span className="material-icons">arrow_back</span>
                  <span className="pl-2">{t('back-to-frontpage')}</span>
                </a>
              </li>
            </ul>
            {currentGid && canManageSubscription(richUser) && (
              <GroupPageNavigation
                gid={currentGid}
                activePage={groupPage}
                hasAdminAccess={hasAdminAccess}
              />
            )}
            {richUser?.currentGroupSettings && richUser.visibleGroups.length > 1 && (
              <>
                <div className="mb-2">
                  <div className="rounded bg-gray-100 p-2 font-bold">
                    {richUser.currentGroupSettings.name}
                  </div>
                  <div className="mt-3">
                    <ul>
                      {richUser.visibleGroups
                        .filter(({ gid }) => gid !== richUser.currentGroupSettings.id)
                        .map(({ gid, groupName }) => {
                          return (
                            <li key={gid} className="py-1">
                              <a
                                href={`/o/${locale}/group/${gid}/${groupPage}`}
                                className="cm-link"
                              >
                                {groupName}
                              </a>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                </div>
              </>
            )}
          </nav>
        </aside>

        <main className="flex-1 px-2 py-2 md:border-l md:px-8">
          <div className="flex flex-row items-center justify-between gap-1">
            <h1 className="py-6 text-2xl">{title}</h1>
          </div>
          <div>{children}</div>
        </main>
      </div>
    </div>
  );
};

const SimpleLayout = ({ children, title }: Props) => (
  <div className="font-quicksand">
    <Head>
      <title>{title}</title>
      <meta charSet="utf-8" />
      <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet"></link>
    </Head>
    {children}
  </div>
);

export default Layout;

export { SimpleLayout };

const Header = ({
  signOut,
  groupPage,
}: {
  signOut: () => void;
  groupPage: 'subscription' | 'member-invite-template';
}) => {
  const { t, locale } = useLocale();
  const { richUser, loading } = useAuthContext();
  const [showMenu, setShowMenu] = useState(false);
  const currentGid = richUser?.currentGroupSettings?.id;
  const currentRole = currentGid ? richUser?.userSettings?.groups?.[currentGid]?.role : undefined;
  const hasAdminAccess = ['owner', 'admin', 'sponsored_admin'].includes(currentRole);

  return (
    <header className="bg-white">
      <div className="flex items-start border-b px-4 py-2 md:px-9 md:py-8">
        <a href="/" className="flex items-center">
          <div className="absolute top-[-200px] md:static">
            <Logo height={45} />
          </div>
          <div className="flex md:hidden">
            <Logo height={25} />
          </div>
          <div className="ml-4 text-lg md:ml-6 md:text-2xl">ChoirMate</div>
        </a>
        <div className="flex flex-1 flex-col items-end justify-end md:hidden">
          <Spinner loading={loading} />
          {richUser && (
            <>
              <button
                className="h-[24px] overflow-hidden md:hidden"
                onClick={() => setShowMenu(!showMenu)}
              >
                <span className="material-icons">{showMenu ? 'close' : 'menu'}</span>
              </button>
              <div
                className={`grid ${
                  showMenu ? `grid-rows-[1fr]` : `grid-rows-[0fr]`
                } gap-2 overflow-clip pt-0 transition-all duration-300 ease-in-out`}
              >
                <div
                  className={`${
                    showMenu ? 'visible opacity-100' : 'invisible opacity-0'
                  } flex h-full min-h-0 flex-col gap-2 transition-all duration-300 ease-in-out`}
                >
                  {richUser?.currentGroupSettings && (
                    <>
                      <div className="mb-2">
                        <div className="rounded bg-gray-100 p-2 font-bold">
                          {richUser.currentGroupSettings.name}
                        </div>
                        {currentGid && canManageSubscription(richUser) && (
                          <GroupPageNavigation
                            gid={currentGid}
                            activePage={groupPage}
                            hasAdminAccess={hasAdminAccess}
                            compact
                          />
                        )}
                        {richUser.visibleGroups.length > 1 && (
                          <div className="mt-3">
                            <div className="text-sm text-gray-500">{t('other-groups')}</div>
                            <ul className="mt-1">
                              {richUser.visibleGroups
                                .filter(({ gid }) => gid !== richUser.currentGroupSettings.id)
                                .map(({ gid, groupName }) => {
                                  return (
                                    <li key={gid} className="py-1">
                                      <a
                                        href={`/o/${locale}/group/${gid}/${groupPage}`}
                                        className="cm-link"
                                      >
                                        {groupName}
                                      </a>
                                    </li>
                                  );
                                })}
                            </ul>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <LogoutButton signOut={signOut} displayName={richUser.user.displayName} />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="hidden flex-1 items-center justify-end md:flex">
          <Spinner loading={loading} />
          {richUser && <LogoutButton signOut={signOut} displayName={richUser.user.displayName} />}
        </div>
      </div>
    </header>
  );
};

const GroupPageNavigation = ({
  gid,
  activePage,
  compact = false,
  hasAdminAccess,
}: {
  gid: string;
  activePage: 'subscription' | 'member-invite-template';
  compact?: boolean;
  hasAdminAccess: boolean;
}) => {
  const { t, locale } = useLocale();
  const [isDebug, setIsDebug] = useState(false);

  useEffect(() => {
    setIsDebug(Boolean(localStorage.getItem('sg.debug')));
  }, []);

  const links = [
    { page: 'subscription' as const, label: t('group-navigation.subscription') },
    ...(isDebug && hasAdminAccess
      ? [
          {
            page: 'member-invite-template' as const,
            label: t('group-navigation.member-invite-template'),
          },
        ]
      : []),
  ];
  return (
    <ul className={compact ? 'mt-3 space-y-1' : 'mb-5 space-y-1'}>
      {links.map(({ page, label }) => (
        <li key={page}>
          <a
            href={`/o/${locale}/group/${gid}/${page}`}
            className={`cm-link inline-flex py-2 ${activePage === page ? 'active' : ''}`}
          >
            {label}
          </a>
        </li>
      ))}
    </ul>
  );
};

const LogoutButton = ({ displayName, signOut }: { displayName: string; signOut: () => void }) => {
  const { t } = useLocale();
  return (
    <div className="flex flex-row gap-3">
      <div className="md:text-xl">{displayName}</div>
      <button
        title={t('log-out')}
        onClick={async () => {
          signOut();
          window.location.href = 'https://web.choirmate.com';
        }}
      >
        <span className="material-icons">logout</span>
      </button>
    </div>
  );
};
